import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/OpenInNew';
import HomeIcon from '@mui/icons-material/Home';
import HelpIcon from '@mui/icons-material/Help';
import ListIcon from '@mui/icons-material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Routes from '../routes/Routes';
import { NavLink, withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectOptions } from '../reducers/optionsSlice';
import { selectUser, toggleDarkMode } from '../reducers/userSlice';

import { useAuth0 } from "@auth0/auth0-react";

import { AccountCircle, Article, ContactSupport, HelpCenter, Info, Logout, ManageHistory, QueryStats } from '@mui/icons-material';
import { FormControlLabel, FormGroup, Link, Menu, MenuItem, SvgIcon, Switch } from '@mui/material';
import { About } from '../components/About';
import { TermsConditions } from '../components/TermsConditions';
import { makeStyles } from '@mui/styles';
import { ManageCDGRJobs } from '../components/ManageCDGRJobs';
import { analysisIcon, economicsIcon, forecastIcon, petroNinjaIcon, realTimeIcon } from '../helpers/icons';


const ENV = process.env.REACT_APP_NODE_ENV;
// const BASE_URL_PN = ENV === 'production' ? process.env.REACT_APP_BASE_URL_PN : process.env.REACT_APP_BASE_URL_PN_STAGING;

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: '60px'
  // width: `calc(${theme.spacing(1)} + 1px)`,
  // [theme.breakpoints.up('sm')]: {
  //   width: `calc(${theme.spacing(9)} + 1px)`,
  // },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const useStyles = makeStyles(theme => ({
  drawerLight: {
    background: theme?.palette?.elevationOne?.light,
    boxShadow: "2px 0px 6px lightgrey"
  },
  drawerDark: {
    background: theme?.palette?.elevationOne?.dark,
    boxShadow: "1px 0px 8px #222"
  }
}));

const NavDrawer = (props) => {
  const theme = useTheme();
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);

  const dispatch = useDispatch();
  const { logout } = useAuth0();

  const options = useSelector(selectOptions);
  const user = useSelector(selectUser);

  const [anchorElHelp, setAnchorElHelp] = React.useState(null);
  const [anchorElAccount, setAnchorElAccount] = React.useState(null);
  const [aboutVisible, setAboutVisible] = React.useState(false);
  const [termsConditionsVisible, setTermsConditionsVisible] = React.useState(false);
  const helpMenuOpen = Boolean(anchorElHelp);
  const accountMenuOpen = Boolean(anchorElAccount);
  
  const [darkMode, setDarkMode] = React.useState(user?.value?.darkMode || false);
  const [manageJobsVisible, setManageJobsVisible] = React.useState(false);

  const demoUser = user?.value?.subscription === 'Demo';

  const handleHelpMenuClick = (event) => {
    setAnchorElHelp(event.currentTarget);
  };
  const handleHelpMenuClose = () => {
    setAnchorElHelp(null);
  };


  const handleAccountMenuClick = (event) => {
    setAnchorElAccount(event.currentTarget);
  };
  const handleAccountMenuClose = () => {
    setAnchorElAccount(null);
  };


  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} style={{backgroundColor: '#0758B7', height: '50px', display: 'flex', justifyContent: 'center'}}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: '20px',
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <img src={process.env.PUBLIC_URL + '/img/athenaxssd_logo_white.png'} alt="" style={{width: 36, marginRight: 20}}></img>
          {/* <Typography variant="h5" noWrap>
            <b>SSD Automation</b>
          </Typography> */}
          
          <div style={{position: 'absolute', right: 20, display: 'flex', alignItems: 'center'}}>
            <Tooltip title="Job management">
              <span><IconButton 
                style={{color: "white", marginRight: 20}} 
                onClick={e => setManageJobsVisible(true)} 
                disabled={user?.value?.email === 'wcap@ssdynamics.com' || (!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes('cdgr-ltd.com') && !user?.value?.email?.includes('dgmc-ltd.com'))}
              >
                <ManageHistory />
              </IconButton></span>
            </Tooltip>
  
            <Tooltip title="Help">
              <IconButton style={{color: "white", marginRight: 20}} onClick={handleHelpMenuClick}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings">
              <Avatar style={{width: '36px', height: '36px', cursor: 'pointer', backgroundColor: '#fff', color: theme.palette.primary.main}} onClick={handleAccountMenuClick}>
                <Typography fontSize={'18px'} fontWeight={'bold'}>{`${user?.value?.email?.substring(0, 2)?.toUpperCase() || ""}`}</Typography>
                {/* <Typography fontSize={'18px'} fontWeight={'bold'}>{`${user?.value?.firstName?.[0] || ""}${user?.value?.lastName?.[0] || ""} `}</Typography> */}
              </Avatar>
            </Tooltip>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open} classes={{paper: (darkMode ? classes.drawerDark : classes.drawerLight)}}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <List>
          {Routes.map((prop, key) => (
            <NavLink to={
              ( 
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && !user?.value?.fbaAccess && prop.sidebarName === 'Home') ||
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && prop.sidebarName === 'Wells') ||
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && prop.sidebarName === 'Forecast') ||
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && prop.sidebarName === 'Economics') ||
                (!user?.value?.fbaAccess && prop.sidebarName === 'FBA') ||
                (!user?.value?.rtfoAccess && prop.sidebarName === 'RTFO')
              ) ? '#' : 
              prop.path} 
              style={{ 
                textDecoration: 'none', 
                color: (!user?.value?.forecastAccess && !user?.value?.fbaAccess && prop.sidebarName === 'Home') ||
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && prop.sidebarName === 'Wells') ||
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && prop.sidebarName === 'Forecast') ||
                (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && prop.sidebarName === 'Economics') ||
                (!user?.value?.fbaAccess && prop.sidebarName === 'FBA') ||
                (!user?.value?.rtfoAccess && prop.sidebarName === 'RTFO') ? 'lightgrey' : 'black' 
              }} key={prop.sidebarName}>
              <Tooltip title={prop.sidebarName} key={key} placement="right">
                <ListItem 
                  // disabled={
                  //   ((prop.sidebarName !== 'Projects' && prop.sidebarName !== 'Home') && projects?.allIds?.length === 0)
                  // } 
                  selected={activeRoute(prop.path)}
                >
                  <ListItemIcon style={{color: activeRoute(prop.path) ? (darkMode ? '#fff' : theme.palette.primary.main) : 'grey'}}>
                    {
                      prop.sidebarName === 'Home' ? <HomeIcon style={{opacity: user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && !user?.value?.fbaAccess && 0.25}} /> :
                      prop.sidebarName === 'Wells' ? <ListIcon style={{opacity: user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && 0.25}} /> :
                      prop.sidebarName === 'Forecast' ? <SvgIcon style={{opacity: user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && 0.25}}>{forecastIcon}</SvgIcon> :
                      prop.sidebarName === 'Economics' ? <SvgIcon style={{opacity: user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess && 0.25}}>{economicsIcon}</SvgIcon> :
                      prop.sidebarName === 'FBA' ? <SvgIcon style={{opacity: !user?.value?.fbaAccess && 0.25}}>{analysisIcon}</SvgIcon> :
                      prop.sidebarName === 'RTFO' ? <SvgIcon style={{opacity: !user?.value?.rtfoAccess && 0.25}}>{realTimeIcon}</SvgIcon> :
                      prop.sidebarName === 'Analytics' ? <QueryStats /> :
                      <></>
                    }
                  </ListItemIcon>
                  <ListItemText primary={prop.sidebarName} />
                </ListItem>
              </Tooltip>
            </NavLink>
          ))}
        </List>
        <Divider style={{margin: 4}} />
        <List>
          
          <Tooltip title={`Go to Petro Ninja`} placement="right">
            <ListItem>
              <ListItemIcon>
                <Link href={getLink('Petro Ninja')} target="_blank" rel="noreferrer" fill={darkMode ? '#fff' : theme.palette.primary.main}>
                  <SvgIcon>{petroNinjaIcon}</SvgIcon>
                </Link>
              </ListItemIcon>
              <ListItemText primary={'Petro Ninja'} />
            </ListItem>
          </Tooltip>
        </List>


        <div style={{position: 'absolute', bottom: 20, left: 0, width: 60}}>
          <Typography style={{fontSize: 12, textAlign: 'center'}} color="textSecondary" >
            {options?.version || ""}
          </Typography>
          <Typography style={{fontSize: 10, textAlign: 'center'}} color="textSecondary">
            {ENV === 'production' ? '' : ENV === 'staging' ? 'STAGING' : 'LOCAL'}
          </Typography>
        </div>
      </Drawer>

      <Menu
        anchorEl={anchorElHelp}
        open={helpMenuOpen}
        onClose={handleHelpMenuClose}
      > 
        <div style={{
          width: 360, 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 10
        }}>
          <MenuItem onClick={handleAboutClick}><Info color="primary" style={{marginRight: 10}} /> About </MenuItem>
          <MenuItem onClick={handleTermsConditionsClick}><Article color="primary" style={{marginRight: 10}} /> Terms and Conditions </MenuItem>
          <Link 
            href='https://ssdprojects.s3.us-east-2.amazonaws.com/Athena+User+Manual_v2.11.pdf' 
            target="_blank" 
            style={{textDecoration: 'none', color: 'textPrimary'}}
          >
            <MenuItem style={{alignItems: 'center'}}>
              <HelpCenter color="primary" style={{marginRight: 10}} /> 
              <Typography style={{color: 'textPrimary', marginRight: 10}}>User Manual</Typography>
              <LinkIcon fontSize="14px" />
            </MenuItem>
          </Link>

          <Divider />
          <Typography fontSize={12} color="textSecondary">NEED HELP?</Typography>

          <div style={{display: 'flex', paddingLeft: 20, marginTop: 10}}>
            <ContactSupport color="primary" style={{fontSize: '36px', marginRight: 10}} /> <Typography fontSize={18} component="span">support@ssdynamics.com</Typography>
          </div>
        </div>
      </Menu>

      <Menu
        anchorEl={anchorElAccount}
        open={accountMenuOpen}
        onClose={handleAccountMenuClose}
      > 
        <div style={{width: 500, display: 'flex', alignItems: 'center'}}>
          <AccountCircle color="primary" style={{marginLeft: 10, marginRight: 10, fontSize: '48px'}} /> 
          <div>
            {/* <Typography style={{fontWeight: 'bold'}}>{user?.value?.firstName} {user?.value?.lastName}</Typography> */}
            {/* <Typography>{user?.value?.company}</Typography> */}
            <Typography style={{fontWeight: 'bold'}}>{user?.value?.email}</Typography>
            <Typography>Role: {demoUser ? 'Demo' : (user?.value?.email !== 'wcap@ssdynamics.com' && (user?.value?.email?.includes('@ssdynamics.com') || 
              user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))) ? 'Admin' : 'User'}</Typography>
          </div>
        </div>
        <Divider style={{marginTop: 10, marginBottom: 10}} />
        <FormGroup style={{marginLeft: 20, marginRight: 10}}>
            <FormControlLabel control={<Switch checked={darkMode} onChange={handleDarkModeToggle} />} label="Dark Mode" disabled={!user?.value?.email?.includes('ssdynamics.com')} />
        </FormGroup>
        {/* <MenuItem onClick={handleAccountMenuClose}><Settings color="primary" style={{marginRight: 10}} /> Settings</MenuItem> */}
        <MenuItem onClick={handleLogout}><Logout color="primary" style={{marginRight: 10}} /> Logout</MenuItem>
      </Menu>
      

      {termsConditionsVisible && <TermsConditions userEmail={user?.value?.email} buttons={false} handleTermsConditionsClose={handleTermsConditionsClose} />}
      {aboutVisible && <About version={options.version} lastUpdated={options.lastUpdated} handleAboutClose={handleAboutClose} />}
      {manageJobsVisible && <ManageCDGRJobs darkMode={darkMode} setManageJobsVisible={setManageJobsVisible} />}
    </Box>
  );

  // FUNCTIONS
  function getLink(name) {
    if (name === 'SSD') return 'https://ssdynamics.com';
    if (name === 'Petro Ninja') return 'https://petroninja.com';
  };

  async function handleLogout() {
    const afterLogoutReturnTo = window.location.origin;
    logout({ logoutParams: { returnTo: afterLogoutReturnTo } });
    // logout({ logoutParams: { returnTo: window.location.origin } })
    // await fetch(`${BASE_URL_PN}/logout`, {
    //   method: 'POST',
    //   credentials: 'include', 
    //   headers: {
    //     'x-api-key': process.env.REACT_APP_API_KEY
    //   }
    // });
    console.log('User signed out');

    // window.location.replace("https://athena.petroninja.com");
  };

  function activeRoute(routeName) {
    return props.location.pathname === routeName ? true : false;
  };

  function handleAboutClick(e) {
    setAboutVisible(true);
    setAnchorElHelp(null);
  };
  function handleAboutClose(e) {
    setAboutVisible(false);
  };
  function handleTermsConditionsClick(e) {
    setTermsConditionsVisible(true);
    setAnchorElHelp(null);
  };
  function handleTermsConditionsClose(e) {
    setTermsConditionsVisible(false);
  };

  function handleDarkModeToggle(e) {
    setDarkMode(e.target.checked);
    dispatch(toggleDarkMode({userEmail: user?.value?.email, darkMode: e.target.checked}));
  };
};

export default withRouter(NavDrawer);
